import { useEffect, useMemo, useRef, useState } from "react";
import { Compass, Crosshair, Lock, Map as MapIcon, Move, Route, Unlock, X, ZoomIn, ZoomOut } from "lucide-react";
import { roman } from "./Ornament";
import { DOMAINS, fullMapGraph, nodeById } from "@/lib/catalog";

const W = 960;
const H = 600;
const PAD = 60;
const FRONTIER_C = "#8A7CC2";
const DOMAIN_KEYS = Object.keys(DOMAINS);

function computeLayout(graph) {
  const nodes = (graph.nodes || []).map((n) => ({ ...n, vx: 0, vy: 0 }));
  const index = new Map(nodes.map((n, i) => [n.id, i]));
  const edges = (graph.edges || []).filter((e) => index.has(e.source) && index.has(e.target));
  const full = !!graph.full;

  nodes.forEach((n, i) => {
    if (full) {
      const di = DOMAIN_KEYS.indexOf(n.cat);
      const a = n.frontier ? i * 2.399963 : (di / DOMAIN_KEYS.length) * Math.PI * 2 + ((i * 2.399963) % 0.9) - 0.45;
      const rad = n.frontier ? 240 : 150 + (i % 5) * 28;
      n.x = W / 2 + Math.cos(a) * rad;
      n.y = H / 2 + Math.sin(a) * rad * 0.62;
    } else {
      const a = i * 2.399963;
      const rad = 140 + (i % 4) * 55;
      n.x = W / 2 + Math.cos(a) * rad;
      n.y = H / 2 + Math.sin(a) * rad;
    }
  });

  const L = full ? 70 : 165;
  const REP = full ? 2600 : 13000;
  const iters = full ? 300 : 420;
  for (let it = 0; it < iters; it++) {
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d2 = Math.max(100, dx * dx + dy * dy);
        const d = Math.sqrt(d2);
        const rep = REP / d2;
        const fx = (dx / d) * rep;
        const fy = (dy / d) * rep;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }
    }
    edges.forEach((e) => {
      const a = nodes[index.get(e.source)];
      const b = nodes[index.get(e.target)];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy) + 0.01;
      const f = (d - L) * (full ? 0.012 : 0.02);
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    });
    nodes.forEach((n) => {
      const g = full ? 0.004 : 0.0015;
      n.vx += (W / 2 - n.x) * g;
      n.vy += (H / 2 - n.y) * g * 1.6;
      n.x += n.vx * 0.82;
      n.y += n.vy * 0.82;
      n.vx *= 0.58;
      n.vy *= 0.58;
    });
  }

  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const s = Math.min((W - PAD * 2) / Math.max(1, maxX - minX), (H - PAD * 2) / Math.max(1, maxY - minY));
  nodes.forEach((n) => {
    n.x = PAD + (n.x - minX) * s + (W - PAD * 2 - (maxX - minX) * s) / 2;
    n.y = PAD + (n.y - minY) * s + (H - PAD * 2 - (maxY - minY) * s) / 2;
  });
  return { nodes, edges, index };
}

const Toggle = ({ on, onChange, label, disabled, testid }) => (
  <button
    data-testid={testid}
    role="switch"
    aria-checked={on}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[0.68rem] font-mono uppercase tracking-[0.14em] transition-colors disabled:opacity-40 ${
      on ? "border-primary/60 text-primary bg-accent/60" : "border-border text-muted-foreground hover:border-primary/40"
    }`}
  >
    <span className={`relative inline-block h-3.5 w-6 rounded-full transition-colors ${on ? "bg-primary" : "bg-border"}`}>
      <span className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-background transition-transform ${on ? "translate-x-3" : "translate-x-0.5"}`} />
    </span>
    {label}
  </button>
);

const Legend = ({ mode, frontier, showRoute }) => (
  <div data-testid="graph-legend" className="absolute bottom-3 right-3 rounded-lg p-3 shadow-lg text-[hsl(230_24%_10%)] bg-[hsl(40_38%_91%)] border border-[hsl(36_40%_60%)] max-w-[11rem]">
    <span className="font-display italic text-xs block mb-2 text-[hsl(36_62%_30%)]">Legend</span>
    <ul className="space-y-1.5 text-[0.66rem] font-mono uppercase tracking-[0.1em]">
      {showRoute && (
        <li className="flex items-center gap-2">
          <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><circle cx="10" cy="10" r="6.5" fill="hsl(40 58% 50%)" stroke="hsl(36 62% 30%)" strokeWidth="1" /></svg>
          Route step
        </li>
      )}
      <li className="flex items-center gap-2">
        <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0">
          {mode === "map" ? (
            <>
              <circle cx="7" cy="10" r="4" fill="#5BA3E8" /><circle cx="13" cy="10" r="4" fill="#F2A93B" fillOpacity="0.9" />
            </>
          ) : (
            <circle cx="10" cy="10" r="5" fill="hsl(40 38% 97%)" stroke="hsl(230 10% 45%)" strokeWidth="1.5" />
          )}
        </svg>
        {mode === "map" ? "Topic · by domain" : "Nearby topic"}
      </li>
      {showRoute && (
        <li className="flex items-center gap-2">
          <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><line x1="2" y1="10" x2="18" y2="10" stroke="hsl(40 58% 45%)" strokeWidth="2" /></svg>
          Route link
        </li>
      )}
      <li className="flex items-center gap-2">
        <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><line x1="2" y1="10" x2="18" y2="10" stroke="hsl(230 10% 45%)" strokeWidth="1.2" strokeDasharray="2 3" /></svg>
        {mode === "map" ? "Prerequisite" : "Meridian (related)"}
      </li>
      {frontier && (
        <li className="flex items-center gap-2" data-testid="legend-frontier">
          <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><circle cx="10" cy="10" r="5" fill="none" stroke={FRONTIER_C} strokeWidth="1.4" strokeDasharray="2 2" /></svg>
          Frontier · unroutable
        </li>
      )}
      <li className="flex items-center gap-2">
        <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0"><circle cx="10" cy="10" r="6" fill="hsl(150 40% 40%)" /><path d="M7 10l2 2 4-4" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" /></svg>
        Sealed
      </li>
    </ul>
  </div>
);

export const GraphPanel = ({ graph, route, sealed, open, initialMode = "route", onClose, onPlanRoute }) => {
  const hasRoute = !!route && !route.draft && !!graph;
  const [mode, setMode] = useState(initialMode);
  const [showFrontier, setShowFrontier] = useState(false);
  const [showRoute, setShowRoute] = useState(true);
  const [interactive, setInteractive] = useState(false);
  const [t, setT] = useState({ x: 0, y: 0, scale: 1 });
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const svgRef = useRef(null);
  const drag = useRef(null);

  const activeGraph = useMemo(
    () => (mode === "map" || !hasRoute ? fullMapGraph(hasRoute ? route : null, showFrontier) : graph),
    [mode, hasRoute, route, graph, showFrontier]
  );
  const layout = useMemo(() => computeLayout(activeGraph || {}), [activeGraph]);
  const posById = useMemo(() => new Map(layout.nodes.map((n) => [n.id, n])), [layout]);
  const isMap = mode === "map" || !hasRoute;
  const routeOn = showRoute && hasRoute;

  useEffect(() => {
    if (open) {
      setMode(hasRoute ? initialMode : "map");
      setInteractive(false);
      setShowFrontier(false);
      setShowRoute(true);
      setT({ x: 0, y: 0, scale: 1 });
      setSelected(null);
      setHovered(null);
    }
  }, [open, graph, initialMode, hasRoute]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el || !interactive) return;
    const onWheel = (e) => {
      e.preventDefault();
      setT((prev) => ({ ...prev, scale: Math.min(3, Math.max(0.5, prev.scale * (e.deltaY < 0 ? 1.12 : 0.89))) }));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [interactive]);

  if (!open) return null;

  const onPointerDown = (e) => {
    if (!interactive) return;
    drag.current = { x: e.clientX, y: e.clientY, tx: t.x, ty: t.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setT((prev) => ({ ...prev, x: drag.current.tx + (e.clientX - drag.current.x), y: drag.current.ty + (e.clientY - drag.current.y) }));
  };
  const onPointerUp = () => (drag.current = null);
  const recenter = () => setT({ x: 0, y: 0, scale: 1 });
  const zoom = (dir) => setT((prev) => ({ ...prev, scale: Math.min(3, Math.max(0.5, prev.scale * (dir > 0 ? 1.15 : 0.85))) }));

  const selNode = selected ? posById.get(selected) : null;
  const selFull = selNode ? nodeById[selNode.id] : null;
  const neighbors = hovered
    ? new Set(layout.edges.flatMap((e) => (e.source === hovered ? [e.target] : e.target === hovered ? [e.source] : [])))
    : null;

  const isRouteNode = (n) => routeOn && n.on_route;
  const nodeOpacity = (n) => {
    if (hovered === n.id || selected === n.id) return 1;
    if (neighbors?.has(n.id)) return 1;
    if (n.frontier) return hovered ? 0.2 : 0.38;
    if (isMap) return hovered ? 0.35 : routeOn && !n.on_route ? 0.6 : 1;
    return isRouteNode(n) ? 1 : 0.45;
  };
  const nodeFill = (n) => {
    if (isRouteNode(n)) return "url(#brass-node)";
    if (n.frontier) return "hsl(var(--background))";
    if (isMap) return DOMAINS[n.cat]?.c || "hsl(var(--card))";
    return "hsl(var(--card))";
  };
  const nodeR = (n) => (isRouteNode(n) ? (isMap ? 10 : 13) : isMap ? (n.hub ? 7.5 : 5.5) : 8);
  const showLabel = (n) => isRouteNode(n) || hovered === n.id || selected === n.id || (isMap && n.hub && !n.frontier);

  const title = isMap ? (
    <>The whole <span className="italic text-primary">Atlas</span></>
  ) : (
    <>Explore how this route <span className="italic text-primary">connects</span></>
  );
  const sub = isMap
    ? `${layout.nodes.filter((n) => !n.frontier).length} catalog topics, coloured by domain${showFrontier ? ` · ${layout.nodes.filter((n) => n.frontier).length} frontier nodes shown dimmed` : ""}. Click any topic to plan a route to it.`
    : "Brass nodes are your route. Everything else is nearby context.";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-md" onClick={onClose} data-testid="graph-overlay-backdrop" />
      <div data-testid="bounded-graph-container" className="relative w-full max-w-5xl bg-card rounded-t-2xl sm:rounded-2xl border border-primary/30 shadow-[0_30px_80px_-30px_hsl(var(--brass-glow)/0.35)] overflow-hidden atlas-rise">
        <div className="flex items-start justify-between gap-4 px-5 sm:px-7 pt-5 pb-4 border-b border-border">
          <div className="min-w-0">
            <span className="eyebrow text-primary">Knowledge web · optional</span>
            <h3 className="font-display font-medium text-2xl sm:text-3xl tracking-tight leading-tight mt-1" data-testid="graph-title">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{sub}</p>
          </div>
          <button data-testid="graph-close-button" onClick={onClose} className="grid place-items-center h-9 w-9 rounded-full border border-border hover:border-primary/60 hover:text-primary transition-colors shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* View controls */}
        <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 pt-4" data-testid="graph-view-controls">
          <div className="inline-flex rounded-full border border-border p-0.5 bg-background/60">
            <button
              data-testid="graph-mode-route"
              disabled={!hasRoute}
              onClick={() => setMode("route")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.68rem] font-mono uppercase tracking-[0.14em] transition-colors disabled:opacity-40 ${!isMap ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Route className="h-3.5 w-3.5" /> Route view
            </button>
            <button
              data-testid="graph-mode-map"
              onClick={() => setMode("map")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.68rem] font-mono uppercase tracking-[0.14em] transition-colors ${isMap ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <MapIcon className="h-3.5 w-3.5" /> Whole map
            </button>
          </div>
          <Toggle testid="graph-toggle-route" on={routeOn} disabled={!hasRoute} onChange={setShowRoute} label="My route" />
          <Toggle testid="graph-toggle-frontier" on={showFrontier} disabled={!isMap} onChange={setShowFrontier} label="Frontier" />
        </div>

        <div className="plate-frame relative bg-background atlas-grid-flat m-4 sm:m-5 rounded-xl border border-border overflow-hidden">
          <svg
            ref={svgRef}
            data-testid="graph-canvas"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-[50vh] sm:h-[52vh] select-none touch-none"
            style={{ cursor: interactive ? (drag.current ? "grabbing" : "grab") : "default" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onClick={() => setSelected(null)}
          >
            <defs>
              <radialGradient id="brass-node" cx="38%" cy="32%" r="72%">
                <stop offset="0%" stopColor="hsl(var(--accent-hover))" />
                <stop offset="60%" stopColor="hsl(var(--primary))" />
                <stop offset="100%" stopColor="hsl(var(--primary) / 0.7)" />
              </radialGradient>
              <filter id="brass-glow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="3.5" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <g transform={`translate(${t.x} ${t.y}) scale(${t.scale})`}>
              <g stroke="hsl(var(--primary))" strokeOpacity="0.1" fill="none" strokeWidth="0.8" pointerEvents="none">
                {[110, 210, 310, 420].map((r) => <circle key={r} cx={W / 2} cy={H / 2} r={r} strokeDasharray="2 7" />)}
                {[0, 30, 60, 90, 120, 150].map((deg) => {
                  const a = (deg * Math.PI) / 180;
                  return <line key={deg} x1={W / 2 - Math.cos(a) * 600} y1={H / 2 - Math.sin(a) * 600} x2={W / 2 + Math.cos(a) * 600} y2={H / 2 + Math.sin(a) * 600} />;
                })}
              </g>

              {layout.edges.map((e, i) => {
                const a = posById.get(e.source);
                const b = posById.get(e.target);
                if (!a || !b) return null;
                const routeEdge = routeOn && a.on_route && b.on_route;
                const active = hovered && (e.source === hovered || e.target === hovered);
                const frontierEdge = e.frontier || a.frontier || b.frontier;
                return (
                  <line
                    key={i}
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={routeEdge || active ? "hsl(var(--primary))" : frontierEdge ? FRONTIER_C : "hsl(var(--graph-muted))"}
                    strokeWidth={routeEdge ? 2.2 : active ? 1.4 : isMap ? 0.9 : 1.1}
                    strokeDasharray={routeEdge ? undefined : e.kind === "hard" && isMap ? undefined : "2 5"}
                    strokeLinecap="round"
                    strokeOpacity={routeEdge ? 0.95 : active ? 0.9 : frontierEdge ? 0.25 : hovered ? 0.18 : isMap ? 0.45 : 0.55}
                    filter={routeEdge ? "url(#brass-glow)" : undefined}
                  />
                );
              })}

              {layout.nodes.map((n) => {
                const r = nodeR(n);
                const onRoute = isRouteNode(n);
                const sel = selected === n.id;
                const isSealed = onRoute && n.step_index != null && sealed?.has(n.step_index);
                return (
                  <g
                    key={n.id}
                    data-testid="graph-node-item"
                    data-node-id={n.id}
                    data-frontier={n.frontier ? "true" : "false"}
                    data-sealed={isSealed ? "true" : "false"}
                    onMouseEnter={() => setHovered(n.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={(ev) => { ev.stopPropagation(); setSelected((s) => (s === n.id ? null : n.id)); }}
                    style={{ cursor: "pointer" }}
                    opacity={nodeOpacity(n)}
                  >
                    <circle cx={n.x} cy={n.y} r={r + 12} fill="transparent" />
                    {sel && <circle cx={n.x} cy={n.y} r={r + 8} fill="none" stroke="hsl(var(--primary))" strokeWidth={1.2} strokeDasharray="2 4" strokeOpacity={0.9} />}
                    {onRoute && <circle cx={n.x} cy={n.y} r={r + 3.5} fill="none" stroke="hsl(var(--primary))" strokeWidth={0.8} strokeOpacity={0.5} />}
                    <circle
                      cx={n.x} cy={n.y} r={r}
                      fill={nodeFill(n)}
                      stroke={onRoute ? "hsl(var(--accent-hover))" : n.frontier ? FRONTIER_C : isMap ? "hsl(var(--background))" : "hsl(var(--graph-muted))"}
                      strokeWidth={onRoute ? 1 : n.frontier ? 1.3 : 1.5}
                      strokeDasharray={n.frontier ? "2 2" : undefined}
                      filter={onRoute ? "url(#brass-glow)" : undefined}
                    />
                    {onRoute && n.step_index != null && (
                      <text x={n.x} y={n.y + 3.5} textAnchor="middle" fontSize={isMap ? "8.5" : "10.5"} fontFamily="Fraunces, Georgia, serif" fontWeight="600" fill="hsl(var(--primary-foreground))" style={{ pointerEvents: "none" }}>
                        {roman(n.step_index + 1)}
                      </text>
                    )}
                    {isSealed && (
                      <g transform={`translate(${n.x + r - 3} ${n.y - r + 3})`} style={{ pointerEvents: "none" }}>
                        <circle r="6.5" fill="hsl(var(--soft))" stroke="hsl(var(--background))" strokeWidth="1.5" />
                        <path d="M-3 0 l2 2.2 4-4.6" fill="none" stroke="hsl(var(--background))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                    )}
                    {showLabel(n) && (
                      <text
                        x={n.x} y={n.y - r - 7} textAnchor="middle"
                        fontSize={isMap ? "11" : "13"} fontFamily="Fraunces, Georgia, serif"
                        fontStyle={onRoute ? "normal" : "italic"} fontWeight={onRoute ? "600" : "400"}
                        fill={n.frontier ? FRONTIER_C : "hsl(var(--foreground))"} stroke="hsl(var(--background))" strokeWidth="4" paintOrder="stroke"
                        style={{ pointerEvents: "none" }}
                      >
                        {n.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

          {!interactive && (
            <div className="absolute inset-0 grid place-items-center bg-background/55 backdrop-blur-[1.5px]" data-testid="graph-lock-overlay">
              <button data-testid="graph-enter-interactive-button" onClick={() => setInteractive(true)} className="btn-brass">
                <Move className="h-4 w-4" /> Enter interactive mode
              </button>
              <p className="mt-2 text-xs font-display italic text-muted-foreground max-w-xs text-center">
                Zoom &amp; pan stay off until you’re ready — so the page never scrolls by accident.
              </p>
            </div>
          )}

          <div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-xl border border-border bg-card/90 backdrop-blur p-1 shadow-sm">
            <button data-testid="graph-recenter-button" onClick={recenter} title="Recenter" className="grid place-items-center h-8 w-8 rounded-lg hover:bg-muted transition-colors"><Crosshair className="h-4 w-4" /></button>
            <button data-testid="graph-zoom-in-button" onClick={() => zoom(1)} disabled={!interactive} title="Zoom in" className="grid place-items-center h-8 w-8 rounded-lg hover:bg-muted transition-colors disabled:opacity-40"><ZoomIn className="h-4 w-4" /></button>
            <button data-testid="graph-zoom-out-button" onClick={() => zoom(-1)} disabled={!interactive} title="Zoom out" className="grid place-items-center h-8 w-8 rounded-lg hover:bg-muted transition-colors disabled:opacity-40"><ZoomOut className="h-4 w-4" /></button>
            <button data-testid="graph-lock-toggle-button" onClick={() => setInteractive((v) => !v)} title={interactive ? "Lock map" : "Unlock map"} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-muted transition-colors">
              {interactive ? <Unlock className="h-4 w-4 text-primary" /> : <Lock className="h-4 w-4" />}
            </button>
          </div>

          <Legend mode={isMap ? "map" : "route"} frontier={isMap && showFrontier} showRoute={routeOn} />

          {selNode && (
            <div data-testid="graph-node-inspector" className="absolute bottom-3 left-3 max-w-[16rem] rounded-xl border border-primary/30 bg-card/95 backdrop-blur p-3.5 shadow-lg">
              <span className="eyebrow" style={{ color: selNode.frontier ? FRONTIER_C : undefined }}>
                <span className={selNode.frontier ? "" : "text-muted-foreground"}>
                  {selNode.frontier
                    ? "Frontier · not routable"
                    : isRouteNode(selNode)
                    ? `Route · step ${roman(selNode.step_index + 1)}${sealed?.has(selNode.step_index) ? " · sealed" : ""}`
                    : `${DOMAINS[selFull?.cat]?.name || "Topic"} · catalog`}
                </span>
              </span>
              <p className="font-display font-medium text-lg leading-tight mt-0.5">{selNode.label}</p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-3">
                {selNode.frontier
                  ? "Speculative layer, shown dimmed for context. Atlas does not plan routes through it."
                  : selFull?.desc}
              </p>
              {!selNode.frontier && !isRouteNode(selNode) && onPlanRoute && (
                <button data-testid="graph-plan-route-button" onClick={() => onPlanRoute(selNode.id)} className="btn-brass mt-3 w-full py-2 text-xs">
                  <Compass className="h-3.5 w-3.5" /> Plan route here
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
